import React, { useMemo } from 'react';

import classNames from 'classnames';

import { useOptionalActualVsBudgetReportContext } from './context/ActualVsBudgetReportContext';
import {
    GROUP_START_CELL,
    isDarkReportRow,
    reportNegativeColourClass,
    reportRowFillClass,
    reportRowIndentClass,
    reportRowWeightClass,
} from './helpers/reportRowStyles';
import { isDetailedRowEmpty } from './helpers/reportRowVisibility';
import { varianceColourClass } from './helpers/varianceColour';
import { COL_COMMITTED, COL_FORECAST, COL_MONTHLY_PHASING, COL_OPPORTUNITY, COL_PERCENT, COL_RISK, COL_UNCOMMITTED } from './types/ReportColumns';
import { DEFAULT_ROW_TYPE, ROW_TOTAL } from './types/ReportRowType';

import { formatFigure, formatNegativeMoney } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

// A zero renders as the zero dash; negatives stay in parentheses.
const formatReportFigure = (value) => formatFigure(value, formatNegativeMoney);

// Server order of the supplementary columns, so the first visible one can carry
// the group's left edge.
const SUPPLEMENTARY_ORDER = [COL_COMMITTED, COL_UNCOMMITTED, COL_PERCENT, COL_RISK, COL_OPPORTUNITY, COL_FORECAST];

const DASH = '—';

// Comparison cells carry a pale tint so they read as a block apart from the
// report version's own figures; total rows take the lighter blue so the tint
// still shows against their fill. Matches the summary table cell for cell — the
// data tiers are white here too, so no per-tier tint is needed.
const AB_TINT = {
    [ROW_TOTAL]: '!tw-bg-blue-75',
};

const AB_TINT_DEFAULT = '!tw-bg-blue-10';

// A comparison figure of null means "no equivalent line in that version" and
// renders as a dash — distinct from a line the comparison version budgets at nil.
const formatComparison = (value) => (value === null || value === undefined ? DASH : formatReportFigure(value));

const ActualVsBudgetReportDetailedTableRow = (props) => {
    const context = useOptionalActualVsBudgetReportContext();
    const datesArray = props.datesArray || context.details?.datesArray || [];
    // The parent table resolves the column set once (live context, or the column
    // set a generated report was stored with) and hands it down, so a row can
    // never render a different set of cells than the header it sits under. The
    // context fallback keeps the tables that render inside the provider without
    // passing the prop working unchanged.
    const columns = props.visibleColumns || context.visibleColumns || {};
    const hideEmptyRows = context.hideEmptyRows ?? false;
    const monthlyPhasing = !!columns[COL_MONTHLY_PHASING];

    // Totals keep their own fill regardless of the tier they were rendered from.
    const rowType = props.isTotal ? ROW_TOTAL : props.rowType || DEFAULT_ROW_TYPE;
    const firstVisibleSupplementary = SUPPLEMENTARY_ORDER.find((key) => columns[key]);
    // The backend attaches this only when a comparison version is selected; a row
    // with no equivalent line in that version carries nulls, not zeros.
    const comparison = props.data?.comparison || { total: null, variance: null };

    const getName = (departmentName, costcenterName, projectName = '') => {
        if (projectName) {
            return projectName;
        }

        if (!!departmentName && !!costcenterName && costcenterName?.toLowerCase() !== 'no costcenter') {
            return departmentName + ' - ' + costcenterName;
        }

        return departmentName || costcenterName;
    };

    const name = props.data
        ? useMemo(
              () =>
                  props.data.code_name
                      ? props.data.code_name
                      : getName(props.data.department_name, props.data.costcenter_name, props.data.project_name ?? null),
              [props.data],
          )
        : '';

    const clickHandler = () => {
        if (props.expandable && typeof props.toggleHandler === 'function') {
            props.toggleHandler();
        }
    };

    // Total and central-contingency rows are rendered without an onDataClick —
    // they aggregate several sources and have no single transaction list to open.
    // Their figure cells still carry the handler, so it has to tolerate that.
    const handleDataClick = (e, month) => {
        e.stopPropagation();

        if (typeof props.onDataClick === 'function') {
            props.onDataClick(month);
        }
    };

    if (!props.show) {
        return null;
    }

    // Totals, the central-contingency line and section rollups always stay
    // visible; only real data rows are suppressed when they have no values.
    const suppressed =
        hideEmptyRows &&
        !props.isTotal &&
        !props.isCentralContingency &&
        isDetailedRowEmpty(props.data, columns, { monthlyPhasing, displayDates: props.displayDates, datesArray });

    if (suppressed) {
        return null;
    }

    // A row the backend flags as existing only in the comparison version has no
    // current-structure figures at all — every cell on this side of the comparison
    // pair dashes, rather than printing zeros that would read as a budgeted nil.
    // (Today: a Central Contingency line the report version does not budget but
    // the comparison version does.)
    const comparisonOnly = !!props.data?.comparison_only;

    // A figure cell: right-aligned monospace, red when the value is negative
    // (already parenthesised by formatNegativeMoney), and optionally opening a
    // column group.
    const figure = (value, { groupStart = false } = {}) => (
        <td
            className={classNames('inbud-numeric-cell', comparisonOnly ? undefined : reportNegativeColourClass(value, rowType), {
                [GROUP_START_CELL]: groupStart,
            })}
        >
            {comparisonOnly ? DASH : formatReportFigure(value)}
        </td>
    );

    // A variance cell is coloured by whether the variance is FAVOURABLE, not by
    // its sign: an underspend is good on expenditure and an overshoot is good on
    // revenue, so the sense inverts per section.
    const variance = (value, { groupStart = false } = {}) => (
        <td
            className={classNames(
                'inbud-numeric-cell',
                comparisonOnly ? undefined : varianceColourClass(value, props.favourableWhenNegative, isDarkReportRow(rowType)),
                {
                    [GROUP_START_CELL]: groupStart,
                },
            )}
        >
            {comparisonOnly ? DASH : formatReportFigure(value)}
        </td>
    );

    return (
        <tr
            className={classNames('tw-text-[12.5px]', reportRowFillClass(rowType), reportRowWeightClass(rowType), {
                'tw-cursor-pointer': props.expandable,
            })}
            onClick={(e) => clickHandler()}
        >
            {/* The label cell inherits the row's own background (it is sticky, so
                it must stay opaque) and carries no rule of its own — the tiers are
                told apart by the indent below, not by a fill or a border. */}
            <td>
                <div className={classNames('tw-flex tw-items-center tw-gap-x-1.5', reportRowIndentClass(rowType))}>
                    {/* Fixed-width slot on every row so names align down the
                        hierarchy whether or not the row is expandable. */}
                    <div className="tw-flex tw-w-3 tw-shrink-0 tw-items-center">
                        {props.expandable &&
                            (props.expanded ? <ChevronDownIcon className="tw-h-1.5 tw-w-1.5" /> : <ChevronRightIcon className="tw-h-1.5 tw-w-1.5" />)}
                    </div>
                    <div>
                        {props.data && props.data.link ? (
                            <a
                                // Only total rows carry a dark fill, so they alone
                                // take white rather than the default charcoal.
                                className={classNames('hover:!tw-text-blue-75', isDarkReportRow(rowType) ? '!tw-text-white' : '!tw-text-charcoal')}
                                href={props.data.link}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {name}
                            </a>
                        ) : (
                            name
                        )}
                    </div>
                </div>
            </td>

            {monthlyPhasing &&
                datesArray.map((month, index) => {
                    return (
                        <td
                            className={classNames('inbud-numeric-cell', { [GROUP_START_CELL]: index === 0 })}
                            key={month}
                            style={{
                                display: props.displayDates[month] ? '' : 'none',
                            }}
                            onClick={(e) => handleDataClick(e, month)}
                        >
                            <div className={comparisonOnly ? undefined : 'tw-cursor-pointer'}>
                                {comparisonOnly ? DASH : formatReportFigure(props.data?.includes?.[month]?.actual_total ?? 0)}
                            </div>
                        </td>
                    );
                })}

            {figure(props.data.month_budget_total, { groupStart: true })}
            {figure(props.data.month_actual_total)}
            {variance(props.data.month_variance)}

            {figure(props.data.period_budget_total, { groupStart: true })}
            <td
                className={classNames('inbud-numeric-cell', comparisonOnly ? undefined : reportNegativeColourClass(props.data.period_actual_total, rowType))}
                onClick={comparisonOnly ? undefined : handleDataClick}
            >
                <div className={comparisonOnly ? undefined : 'tw-cursor-pointer'}>
                    {comparisonOnly ? DASH : formatReportFigure(props.data.period_actual_total)}
                </div>
            </td>
            {variance(props.data.period_variance)}

            {figure(props.data.budget_total, { groupStart: true })}
            {figure(props.data.actual_total)}
            {variance(props.data.total_variance)}

            {/* Lifetime comparison-version columns, rendered only when a
                "Compare to" version is selected. */}
            {props.comparisonActive && (
                <>
                    <td className={classNames('inbud-numeric-cell', AB_TINT[rowType] || AB_TINT_DEFAULT)}>{formatComparison(comparison.total)}</td>
                    <td
                        className={classNames(
                            'inbud-numeric-cell',
                            AB_TINT[rowType] || AB_TINT_DEFAULT,
                            varianceColourClass(comparison.variance, props.favourableWhenNegative, isDarkReportRow(rowType)),
                        )}
                    >
                        {formatComparison(comparison.variance)}
                    </td>
                </>
            )}

            {columns[COL_COMMITTED] && figure(props.data.committed_total, { groupStart: firstVisibleSupplementary === COL_COMMITTED })}
            {columns[COL_UNCOMMITTED] && figure(props.data.uncommitted_total, { groupStart: firstVisibleSupplementary === COL_UNCOMMITTED })}
            {columns[COL_PERCENT] && figure(props.data.uncommitted_percent, { groupStart: firstVisibleSupplementary === COL_PERCENT })}
            {columns[COL_RISK] && figure(props.data.risk ?? 0, { groupStart: firstVisibleSupplementary === COL_RISK })}
            {columns[COL_OPPORTUNITY] && figure(props.data.opp ?? 0, { groupStart: firstVisibleSupplementary === COL_OPPORTUNITY })}
            {columns[COL_FORECAST] && figure(props.data.afc ?? 0, { groupStart: firstVisibleSupplementary === COL_FORECAST })}
        </tr>
    );
};

export default ActualVsBudgetReportDetailedTableRow;
